import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, Plus, X } from 'lucide-react';
import type { ChannelRef } from '../../types';

interface AdditionalChannelsFieldProps<T extends ChannelRef> {
  channels: T[];
  onChange: (channels: T[]) => void;
  // The channel already being reported on / charted - excluded from the
  // picker so you can't "add" it to itself.
  excludeId: string;
  max?: number;
  pickerTitle: string;
  // What the selection is *for* - shown in the picker's "Up to N
  // additional ___s" cap notice, e.g. "report" or "chart".
  noun: string;
  // Optional trailing help line, given the current selection count
  // (only called when count > 0). Returning null/undefined hides it.
  helpText?: (count: number) => string | null | undefined;
  // Shown as a (?) tooltip instead of a description line.
  info?: string;
}

// A "+"-to-add, chip-to-remove multi-channel selector backed by
// ChannelPickerScreen.tsx - shared by ReportsScreen.tsx (Multi-Channel
// Reports, full `Channel[]`) and ChartScreen.tsx (multi-channel chart
// comparison, the leaner persisted `ChannelRef[]` - see that type's
// comment), which otherwise differ only in copy/cap and what they do with
// the result. Generic over T so each caller's own element type round-trips
// through the picker unchanged - the picker itself always deals in full
// Channel objects (it has nothing else to work with), and a full Channel
// satisfies either T structurally. Owns the round trip to /channels/pick
// and back (reading `selectedChannels` off location.state) - the caller
// only owns the `channels` array itself and resets it when its own primary
// channel changes.
export function AdditionalChannelsField<T extends ChannelRef>({
  channels,
  onChange,
  excludeId,
  max = 4,
  pickerTitle,
  noun,
  helpText,
  info,
}: AdditionalChannelsFieldProps<T>) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const returned = (location.state as { selectedChannels?: T[] } | null)?.selectedChannels;
    if (returned) onChange(returned);
    // Only ever meant to react to a fresh return trip from the picker,
    // not to `onChange` identity changes (an inline arrow function at
    // most call sites would otherwise re-run this every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const help = channels.length > 0 ? helpText?.(channels.length) : null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {channels.map((ch) => (
          <span
            key={ch.id}
            className="inline-flex items-center gap-1 rounded-full border border-black/15 dark:border-white/15 pl-2.5 pr-1.5 py-1 text-[11px] text-black/70 dark:text-white/70"
          >
            {ch.title}
            <button
              type="button"
              onClick={() => onChange(channels.filter((c) => c.id !== ch.id))}
              aria-label={`Remove ${ch.title}`}
              className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        {channels.length < max && (
          <button
            type="button"
            onClick={() =>
              navigate('/channels/pick', {
                state: {
                  selected: channels,
                  excludeId,
                  returnTo: location.pathname,
                  title: pickerTitle,
                  maxAdditional: max,
                  noun,
                },
              })
            }
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-black/20 dark:border-white/20 px-2.5 py-1 text-[11px] text-black/50 dark:text-white/50 hover:border-blue-400 hover:text-blue-600"
          >
            <Plus size={11} />
            {channels.length === 0 ? 'Add channel' : 'Add another'}
          </button>
        )}
        {info && (
          <span
            role="img"
            tabIndex={0}
            aria-label={info}
            title={info}
            className="inline-flex text-black/40 dark:text-white/40 hover:text-blue-600 cursor-help"
          >
            <HelpCircle size={14} />
          </span>
        )}
      </div>
      {help && <span className="text-[11px] text-black/40 dark:text-white/40">{help}</span>}
    </div>
  );
}
